import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { client } from '@frontend/features/common/services';
import { catalogAdminKey, catalogAdminPath } from './queries';
import { DestinationPicker } from './DestinationPicker';
import {
  InventoryFilters,
  InventoryHotel,
  InventoryOptions,
  InventoryPage,
  inventoryOffset,
  inventoryParams,
  inventoryRequest,
  inventoryState,
} from './inventory';

export function CatalogInventory() {
  const [params, setParams] = useSearchParams();
  const filters = inventoryState(params),
    offset = inventoryOffset(params);
  return (
    <InventoryView
      key={params.toString()}
      filters={filters}
      offset={offset}
      apply={(next, page = 0) => setParams(inventoryParams(next, page))}
    />
  );
}
function InventoryView({
  filters,
  offset,
  apply,
}: {
  filters: InventoryFilters;
  offset: number;
  apply: (filters: InventoryFilters, offset?: number) => void;
}) {
  const { t, i18n } = useTranslation();
  const label = (key: string) => t(`hotelInventory.${key}`);
  const [draft, setDraft] = useState(filters);
  const options = useQuery({
    queryKey: [...catalogAdminKey, 'inventory-options'],
    queryFn: async () => (await client.get<InventoryOptions>('hotel-catalog-admin/hotels/filters')).data,
    staleTime: 60000,
    retry: false,
  });
  const request = inventoryRequest(filters, offset);
  const page = useQuery({
    queryKey: [...catalogAdminKey, 'inventory', request],
    queryFn: async () => (await client.get<InventoryPage>('hotel-catalog-admin/hotels', { params: request })).data,
    retry: false,
  });
  const countries = options.data?.data.country_codes ?? [];
  const accounts = (options.data?.data.connections ?? []).filter(
    (a) => !draft.connector || a.connector === draft.connector,
  );
  const account = accounts.find((a) => a.connector_account_id === draft.connector_account_id);
  const countryName = (code: string) => {
    try {
      return new Intl.DisplayNames([i18n.language || 'es'], { type: 'region' }).of(code) || code;
    } catch {
      return code;
    }
  };
  const change = (key: keyof InventoryFilters, value: string) =>
    setDraft((current) => {
      const next = { ...current, [key]: value };
      if (key === 'connector') {
        next.connector_account_id = '';
        next.access_code = '';
      }
      if (key === 'connector_account_id') next.access_code = '';
      if (key === 'has_connection' && value === 'false') next.supplier_hotel_code = '';
      if (key === 'country_code') {
        next.geo_place_id = '';
        next.destination_label = '';
      }
      return next;
    });
  const connectionCriteria = [
    'connector',
    'connector_account_id',
    'access_code',
    'supplier_code',
    'supplier_hotel_code',
  ].some((key) => draft[key as keyof InventoryFilters]);
  const select = (key: keyof InventoryFilters, values: Array<[string, string]>, disabled = false) => (
    <TextField
      select
      fullWidth
      size="small"
      label={label(key)}
      value={key === 'has_connection' && connectionCriteria && !draft[key] ? 'true' : draft[key]}
      disabled={disabled}
      onChange={(e) => change(key, e.target.value)}
    >
      {(key !== 'has_connection' || !connectionCriteria) && <MenuItem value="">{label('all')}</MenuItem>}
      {values.map(([value, name]) => (
        <MenuItem value={value} key={value}>
          {name}
        </MenuItem>
      ))}
    </TextField>
  );
  const connectorName = (value: string) => (value === 'hotelbeds' ? 'Hotelbeds' : 'Travelgate');
  const connections = (hotel: InventoryHotel) => (
    <Stack spacing={0.75}>
      {hotel.connections.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {label('noConnections')}
        </Typography>
      )}
      {hotel.connections.map((c, index) => (
        <Box key={index}>
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
            {c.account_name} · {connectorName(c.connector)}
            {c.access_code ? ` · ${c.access_code}` : ''}
            {c.account_status === 'disabled' ? ` · ${label('disabled')}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {c.supplier_code || c.provider_code ? `${c.supplier_code || c.provider_code} · ` : ''}
            {label('providerCode')}: {c.native_hotel_code || c.hotel_code}
            {c.native_hotel_code && c.native_hotel_code !== c.hotel_code
              ? ` · ${label('connectionCode')}: ${c.hotel_code}`
              : ''}
          </Typography>
        </Box>
      ))}
      {hotel.connections_count > hotel.connections.length && (
        <Typography variant="caption">
          {t('hotelInventory.moreConnections', { count: hotel.connections_count - hotel.connections.length })}
        </Typography>
      )}
    </Stack>
  );
  const content = (hotel: InventoryHotel) => (
    <Stack spacing={1}>
      <Stack direction="row" gap={0.5} flexWrap="wrap">
        <Chip
          size="small"
          variant="outlined"
          color={hotel.published ? 'success' : 'default'}
          label={label(hotel.published ? 'publishedYes' : 'publishedNo')}
        />
        {!hotel.main_image && <Chip size="small" variant="outlined" label={label('noImage')} />}
        {!hotel.has_location && <Chip size="small" variant="outlined" label={label('noLocation')} />}
      </Stack>
      <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>
        FastX: {hotel.fastx_codes.length ? hotel.fastx_codes.join(', ') : label('noCode')}
      </Typography>
    </Stack>
  );
  const identity = (hotel: InventoryHotel) => (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Avatar
        variant="rounded"
        src={hotel.main_image ?? undefined}
        alt=""
        imgProps={{ loading: 'lazy', referrerPolicy: 'no-referrer' }}
        sx={{ width: 56, height: 56 }}
      >
        {hotel.display_name.slice(0, 1)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Link
          component={RouterLink}
          to={`${catalogAdminPath}/hotels/${encodeURIComponent(hotel.hotel_id)}`}
          state={{ inventorySearch: inventoryParams(filters, offset).toString() }}
          underline="hover"
          sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}
        >
          {hotel.display_name}
        </Link>
        <Typography variant="body2" color="text.secondary">
          {[hotel.locality, hotel.country_code ? countryName(hotel.country_code) : null].filter(Boolean).join(' · ') ||
            label('noLocationName')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          {label('ownId')}: {hotel.hotel_id}
        </Typography>
      </Box>
    </Stack>
  );
  const state = (hotel: InventoryHotel) => (
    <Stack spacing={0.5} alignItems="flex-start">
      <Chip size="small" color={hotel.status === 'active' ? 'success' : 'default'} label={label(hotel.status)} />
      {hotel.catalog_status === null && <Typography variant="caption">{label('noCommercialEntry')}</Typography>}
    </Stack>
  );
  const changed = inventoryParams(draft).toString() !== inventoryParams(filters).toString();
  return (
    <Stack spacing={2}>
      <Typography color="text.secondary">{label('description')}</Typography>
      <Paper
        variant="outlined"
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          apply(draft);
        }}
        sx={{ p: 2, '& .MuiInputBase-input': { fontSize: 16 }, '& .MuiSelect-select': { fontSize: 16 } }}
      >
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {label('propertyFilters')}
        </Typography>
        <Box
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '2fr 1fr 1.5fr 1fr' }, gap: 2 }}
        >
          <TextField
            size="small"
            label={label('q')}
            value={draft.q}
            inputProps={{ maxLength: 128 }}
            onChange={(e) => change('q', e.target.value)}
          />
          {select(
            'country_code',
            [...new Set([...countries, ...(draft.country_code ? [draft.country_code] : [])])].map((c) => [
              c,
              countryName(c),
            ]),
          )}
          <DestinationPicker
            value={
              draft.geo_place_id
                ? {
                  id: draft.geo_place_id,
                  label: draft.destination_label || draft.geo_place_id,
                  country_code: draft.country_code || null,
                  locality: null,
                }
                : null
            }
            disabled={false}
            onChange={(v) =>
              setDraft((current) => ({
                ...current,
                geo_place_id: v?.id ?? '',
                destination_label: v?.label ?? '',
                country_code: v?.country_code ?? current.country_code,
              }))
            }
          />
          {select('status', [
            ['active', label('active')],
            ['inactive', label('inactive')],
          ])}
        </Box>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          {label('destinationHint')}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {label('connectionFilters')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4,1fr)' }, gap: 2 }}>
          {select('connector', [
            ['hotelbeds', 'Hotelbeds'],
            ['travelgate', 'Travelgate'],
          ])}
          {select(
            'connector_account_id',
            accounts.map((a) => [
              a.connector_account_id,
              `${a.display_name} · ${connectorName(a.connector)}${a.status === 'disabled' ? ` · ${label('disabled')}` : ''}`,
            ]),
            options.isLoading,
          )}
          {select(
            'access_code',
            (account?.access_codes ?? []).map((a) => [a, a]),
            account?.connector !== 'travelgate',
          )}
          {select('has_connection', [
            ['true', label('withConnection')],
            ['false', label('withoutConnection')],
          ])}
          <TextField
            size="small"
            label={label('supplier_code')}
            value={draft.supplier_code}
            inputProps={{ maxLength: 512 }}
            onChange={(e) => change('supplier_code', e.target.value)}
          />
          <TextField
            size="small"
            label={label('supplier_hotel_code')}
            value={draft.supplier_hotel_code}
            inputProps={{ maxLength: 512 }}
            disabled={draft.has_connection === 'false'}
            onChange={(e) => change('supplier_hotel_code', e.target.value)}
          />
          {select('has_fastx', [
            ['true', label('withCode')],
            ['false', label('withoutCode')],
          ])}
          {select('published', [
            ['true', label('publishedYes')],
            ['false', label('publishedNo')],
          ])}
        </Box>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          {label('connectionHint')}
        </Typography>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 2 }}>
          <Button type="submit" variant="contained">
            {label('apply')}
          </Button>
          <Button type="button" onClick={() => apply(inventoryState(new URLSearchParams()))}>
            {label('clear')}
          </Button>
          {changed && (
            <Typography variant="caption" role="status">
              {label('unapplied')}
            </Typography>
          )}
        </Stack>
      </Paper>
      {options.isError && (
        <Alert severity="warning" action={<Button onClick={() => options.refetch()}>{label('retry')}</Button>}>
          {label('optionsError')}
        </Alert>
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">{label('results')}</Typography>
        <Button disabled={page.isFetching} onClick={() => page.refetch()}>
          {label('refresh')}
        </Button>
      </Stack>
      {page.isError ? (
        <Alert severity="error" action={<Button onClick={() => page.refetch()}>{label('retry')}</Button>}>
          {label('loadError')}
        </Alert>
      ) : page.isFetching ? (
        <Stack direction="row" spacing={1} alignItems="center" role="status">
          <CircularProgress size={20} />
          <Typography>{label('loading')}</Typography>
        </Stack>
      ) : !page.data?.data.length ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6">{label('empty')}</Typography>
          <Typography color="text.secondary">{label('emptyHint')}</Typography>
          <Button onClick={() => apply(inventoryState(new URLSearchParams()))}>{label('clear')}</Button>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' } }}>
            <Table aria-label={label('results')}>
              <TableHead>
                <TableRow>
                  {['hotel', 'status', 'connections', 'content'].map((k) => (
                    <TableCell key={k}>{label(k)}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {page.data.data.map((h) => (
                  <TableRow key={h.hotel_id} hover>
                    <TableCell sx={{ width: '32%' }}>{identity(h)}</TableCell>
                    <TableCell>{state(h)}</TableCell>
                    <TableCell sx={{ maxWidth: 330 }}>{connections(h)}</TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>{content(h)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack spacing={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
            {page.data.data.map((h) => (
              <Paper variant="outlined" key={h.hotel_id} sx={{ p: 2 }}>
                <Stack spacing={2}>
                  {identity(h)}
                  {state(h)}
                  <Divider />
                  {connections(h)}
                  {content(h)}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </>
      )}
      <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center" flexWrap="wrap">
        <Button disabled={offset === 0 || page.isFetching} onClick={() => apply(filters, Math.max(0, offset - 25))}>
          {label('previous')}
        </Button>
        <Typography variant="body2" aria-live="polite">
          {!page.isError && !page.isFetching && page.data?.data.length
            ? t('hotelInventory.range', { from: offset + 1, to: offset + page.data.data.length })
            : ''}
        </Typography>
        <Button
          disabled={page.isError || page.isFetching || !page.data?.meta.has_more || offset >= 10000000}
          onClick={() => apply(filters, offset + 25)}
        >
          {label('next')}
        </Button>
      </Stack>
    </Stack>
  );
}
